import React, { useState, useEffect } from 'react';
import { ShoppingBag, User, Heart, Search, Menu, X, ArrowRight, ChevronRight, Play, Star, Plus, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template18 = () => {
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [hoveredProduct, setHoveredProduct] = useState(null);

    const heroSlides = [
        { image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&h=1080&fit=crop', subtitle: 'New Collection', title: 'Autumn Winter 2025', cta: 'Discover Now' },
        { image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=1920&h=1080&fit=crop', subtitle: 'Limited Edition', title: 'Designer Series', cta: 'Shop Collection' },
    ];

    const categories = [
        { name: 'Dresses', count: 156, image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=600&h=800&fit=crop' },
        { name: 'Separates', count: 234, image: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=600&h=800&fit=crop' },
        { name: 'Outerwear', count: 89, image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=600&h=800&fit=crop' },
        { name: 'Occasion', count: 124, image: 'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=600&h=800&fit=crop' },
    ];

    const products = [
        { name: 'Silk Wrap Dress', price: 18500, color: 'Midnight', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=500&h=700&fit=crop', isNew: true },
        { name: 'Tailored Blazer', price: 24000, color: 'Charcoal', image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=500&h=700&fit=crop', isNew: true },
        { name: 'Pleated Midi Skirt', price: 12500, color: 'Champagne', image: 'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=500&h=700&fit=crop', isNew: false },
        { name: 'Cashmere Sweater', price: 19800, color: 'Ivory', image: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=500&h=700&fit=crop', isNew: false },
        { name: 'Embroidered Kurta', price: 15500, color: 'Sage', image: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=500&h=700&fit=crop', isNew: true },
        { name: 'Wide Leg Trousers', price: 13200, color: 'Black', image: 'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1506629082955-511b1aa562c8?w=500&h=700&fit=crop', isNew: false },
        { name: 'Satin Blouse', price: 11800, color: 'Blush', image: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=500&h=700&fit=crop', isNew: false },
        { name: 'Linen Dress', price: 16500, color: 'Natural', image: 'https://images.unsplash.com/photo-1594040226829-7f251ab46d80?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=500&h=700&fit=crop', isNew: true },
    ];

    const testimonials = [
        { text: 'The quality is exceptional. Every piece feels luxurious and fits perfectly.', name: 'Meera Kapoor', city: 'Mumbai', rating: 5 },
        { text: 'Avante has become my go-to for elegant occasion wear. Impeccable service.', name: 'Ananya Sharma', city: 'Delhi', rating: 5 },
        { text: 'Finally found a brand that understands sophisticated, modern Indian fashion.', name: 'Priya Iyer', city: 'Bangalore', rating: 5 },
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 6000);
        return () => clearInterval(timer);
    }, [heroSlides.length]);

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* Top Bar */}
            <div className="bg-zinc-900 text-white text-center py-2.5 text-xs tracking-[0.2em]">
                COMPLIMENTARY SHIPPING ON ORDERS OVER ₹5,000
            </div>

            {/* Header */}
            <header className="bg-white sticky top-0 z-50 border-b border-zinc-100">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="flex items-center justify-between px-6 lg:px-12 py-4">
                        {/* Mobile Menu */}
                        <button className="lg:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                            {mobileMenuOpen ? <X className="w-5 h-5 text-zinc-900" /> : <Menu className="w-5 h-5 text-zinc-900" />}
                        </button>

                        {/* Desktop Nav Left */}
                        <nav className="hidden lg:flex items-center gap-8">
                            <button onClick={() => navigate('/template18/shop')} className="text-sm text-zinc-700 hover:text-zinc-900 transition-colors tracking-wide flex items-center gap-1">
                                Shop <ChevronDown className="w-3 h-3" />
                            </button>
                            <button className="text-sm text-zinc-700 hover:text-zinc-900 transition-colors tracking-wide">
                                New Arrivals
                            </button>
                            <button className="text-sm text-zinc-700 hover:text-zinc-900 transition-colors tracking-wide">
                                Collections
                            </button>
                            <button className="text-sm text-zinc-700 hover:text-zinc-900 transition-colors tracking-wide">
                                The Edit
                            </button>
                        </nav>

                        {/* Logo */}
                        <h1 onClick={() => navigate('/template18')} className="text-2xl lg:text-3xl font-light tracking-[0.25em] text-zinc-900 cursor-pointer">
                            AVANTE
                        </h1>

                        {/* Icons Right */}
                        <div className="flex items-center gap-5">
                            <Search className="w-5 h-5 text-zinc-700 cursor-pointer hover:text-zinc-900 transition-colors" />
                            <User className="w-5 h-5 text-zinc-700 cursor-pointer hover:text-zinc-900 transition-colors hidden sm:block" onClick={() => navigate('/template18/profile')} />
                            <Heart className="w-5 h-5 text-zinc-700 cursor-pointer hover:text-zinc-900 transition-colors hidden sm:block" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template18/cart')}>
                                <ShoppingBag className="w-5 h-5 text-zinc-700 hover:text-zinc-900 transition-colors" />
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-zinc-900 text-white text-[10px] rounded-full flex items-center justify-center">0</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="lg:hidden bg-white border-t border-zinc-100">
                        <nav className="px-6 py-8 space-y-5">
                            <button onClick={() => { navigate('/template18/shop'); setMobileMenuOpen(false); }} className="block text-base text-zinc-800">Shop</button>
                            <button className="block text-base text-zinc-800">New Arrivals</button>
                            <button className="block text-base text-zinc-800">Collections</button>
                            <button onClick={() => { navigate('/template18/contact'); setMobileMenuOpen(false); }} className="block text-base text-zinc-800">Contact</button>
                        </nav>
                    </div>
                )}
            </header>

            {/* Hero Slideshow */}
            <section className="relative h-[95vh]">
                {heroSlides.map((slide, idx) => (
                    <div
                        key={idx}
                        className={`absolute inset-0 transition-opacity duration-1000 ${currentSlide === idx ? 'opacity-100' : 'opacity-0'}`}
                    >
                        <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/60 via-zinc-900/30 to-transparent"></div>
                    </div>
                ))}

                <div className="absolute inset-0 flex items-center">
                    <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 w-full">
                        <div className="max-w-xl">
                            <p className="text-zinc-300 text-sm tracking-[0.4em] mb-4">{heroSlides[currentSlide].subtitle.toUpperCase()}</p>
                            <h2 className="text-5xl lg:text-7xl font-extralight text-white mb-8 leading-[1.1] tracking-wide">
                                {heroSlides[currentSlide].title}
                            </h2>
                            <button
                                onClick={() => navigate('/template18/shop')}
                                className="group px-10 py-4 bg-white text-zinc-900 text-sm tracking-[0.15em] hover:bg-zinc-100 transition-colors inline-flex items-center gap-4"
                            >
                                {heroSlides[currentSlide].cta.toUpperCase()}
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Slide Indicators */}
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-3">
                    {heroSlides.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentSlide(idx)}
                            className={`h-0.5 transition-all duration-500 ${currentSlide === idx ? 'w-12 bg-white' : 'w-6 bg-white/40'}`}
                        />
                    ))}
                </div>
            </section>

            {/* Category Grid */}
            <section className="py-20 lg:py-28">
                <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-14">
                        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-3">DISCOVER</p>
                        <h2 className="text-3xl lg:text-5xl font-extralight text-zinc-900 tracking-wide">Shop by Category</h2>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {categories.map((cat, idx) => (
                            <div key={idx} className="group cursor-pointer">
                                <div className="relative aspect-[3/4] overflow-hidden mb-4">
                                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    <div className="absolute inset-0 bg-zinc-900/0 group-hover:bg-zinc-900/20 transition-colors duration-500"></div>
                                </div>
                                <div className="text-center">
                                    <h3 className="text-lg font-light text-zinc-900 tracking-wide">{cat.name}</h3>
                                    <p className="text-xs text-zinc-500 mt-1">{cat.count} items</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Video Section */}
            <section className="relative h-[80vh] bg-zinc-900">
                <img
                    src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1600&h=900&fit=crop"
                    alt="Campaign Video"
                    className="w-full h-full object-cover opacity-70"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                        <button className="w-24 h-24 bg-white rounded-full flex items-center justify-center mb-8 mx-auto hover:scale-110 transition-transform shadow-2xl">
                            <Play className="w-10 h-10 text-zinc-800 ml-1" />
                        </button>
                        <p className="text-white/70 text-xs tracking-[0.4em] mb-3">CAMPAIGN VIDEO</p>
                        <h2 className="text-4xl lg:text-6xl font-extralight text-white tracking-wide">The New Chapter</h2>
                    </div>
                </div>
            </section>

            {/* Featured Products */}
            <section className="py-20 lg:py-28 bg-zinc-50">
                <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
                    <div className="flex items-end justify-between mb-14">
                        <div>
                            <p className="text-zinc-500 text-xs tracking-[0.3em] mb-3">FEATURED</p>
                            <h2 className="text-3xl lg:text-5xl font-extralight text-zinc-900 tracking-wide">New Arrivals</h2>
                        </div>
                        <button onClick={() => navigate('/template18/shop')} className="hidden md:flex items-center gap-2 text-sm text-zinc-700 hover:text-zinc-900 transition-colors tracking-wide">
                            View All <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {products.map((product, idx) => (
                            <div
                                key={idx}
                                className="group"
                                onMouseEnter={() => setHoveredProduct(idx)}
                                onMouseLeave={() => setHoveredProduct(null)}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden mb-4 bg-zinc-100">
                                    <img
                                        src={hoveredProduct === idx ? product.hoverImage : product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-all duration-700"
                                    />
                                    {product.isNew && (
                                        <span className="absolute top-4 left-4 bg-zinc-900 text-white text-[10px] tracking-widest px-3 py-1.5">NEW</span>
                                    )}
                                    <button className="absolute top-4 right-4 w-9 h-9 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-sm">
                                        <Heart className="w-4 h-4 text-zinc-700" />
                                    </button>
                                    <button className="absolute bottom-4 left-4 right-4 py-3.5 bg-white text-zinc-900 text-xs tracking-[0.15em] opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 flex items-center justify-center gap-2">
                                        <Plus className="w-3 h-3" /> ADD TO BAG
                                    </button>
                                </div>
                                <div className="text-center">
                                    <p className="text-[11px] text-zinc-500 tracking-wider mb-1">{product.color.toUpperCase()}</p>
                                    <h3 className="text-sm text-zinc-900 mb-1">{product.name}</h3>
                                    <p className="text-sm text-zinc-700">₹{product.price.toLocaleString()}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-12 md:hidden">
                        <button onClick={() => navigate('/template18/shop')} className="text-sm text-zinc-800 tracking-wider border-b border-zinc-400 pb-1">
                            VIEW ALL PRODUCTS
                        </button>
                    </div>
                </div>
            </section>

            {/* Split Feature */}
            <section className="grid grid-cols-1 lg:grid-cols-2">
                <div className="aspect-square lg:aspect-auto">
                    <img
                        src="https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=900&h=900&fit=crop"
                        alt="The Edit"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="flex items-center justify-center py-20 lg:py-0 px-8 lg:px-20 bg-zinc-100">
                    <div className="max-w-md">
                        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-4">THE AVANTE EDIT</p>
                        <h2 className="text-3xl lg:text-4xl font-extralight text-zinc-900 mb-6 leading-relaxed tracking-wide">
                            Curated for the Modern Woman
                        </h2>
                        <p className="text-zinc-600 leading-relaxed mb-8">
                            Discover our carefully curated selection of pieces designed for versatility and elegance.
                            Each item is chosen for its impeccable quality and timeless appeal.
                        </p>
                        <button className="text-sm text-zinc-900 tracking-[0.15em] border-b border-zinc-900 pb-1 hover:text-zinc-600 hover:border-zinc-600 transition-colors">
                            EXPLORE THE EDIT
                        </button>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-20 lg:py-28">
                <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-14">
                        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-3">TESTIMONIALS</p>
                        <h2 className="text-3xl lg:text-5xl font-extralight text-zinc-900 tracking-wide">What They Say</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
                        {testimonials.map((item, idx) => (
                            <div key={idx} className="text-center">
                                <div className="flex justify-center gap-1 mb-6">
                                    {[...Array(item.rating)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 fill-zinc-800 text-zinc-800" />
                                    ))}
                                </div>
                                <p className="text-zinc-700 leading-relaxed mb-6 italic text-lg font-light">"{item.text}"</p>
                                <p className="text-zinc-900 tracking-wide">{item.name}</p>
                                <p className="text-zinc-500 text-sm">{item.city}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Newsletter */}
            <section className="py-20 lg:py-24 bg-zinc-900 text-white">
                <div className="max-w-xl mx-auto px-6 text-center">
                    <p className="text-zinc-400 text-xs tracking-[0.3em] mb-3">NEWSLETTER</p>
                    <h2 className="text-2xl lg:text-4xl font-extralight mb-4 tracking-wide">Stay Connected</h2>
                    <p className="text-zinc-400 text-sm mb-8">
                        Subscribe for exclusive access to new collections, private sales, and style updates.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-5 py-4 bg-zinc-800 border-0 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-600"
                        />
                        <button className="px-8 py-4 bg-white text-zinc-900 text-xs tracking-[0.15em] hover:bg-zinc-200 transition-colors">
                            SUBSCRIBE
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white border-t border-zinc-200">
                <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-10 lg:gap-16">
                        <div className="md:col-span-1">
                            <h3 className="text-xl font-light tracking-[0.2em] mb-6">AVANTE</h3>
                            <p className="text-zinc-600 text-sm leading-relaxed">
                                Refined elegance for the modern woman. Premium fashion crafted with intention.
                            </p>
                        </div>

                        <div>
                            <h4 className="text-xs tracking-[0.15em] mb-5 text-zinc-500">SHOP</h4>
                            <ul className="space-y-3 text-sm">
                                <li><span onClick={() => navigate('/template18/shop')} className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">All Products</span></li>
                                <li><span className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">New Arrivals</span></li>
                                <li><span className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">Collections</span></li>
                                <li><span className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">Sale</span></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-xs tracking-[0.15em] mb-5 text-zinc-500">HELP</h4>
                            <ul className="space-y-3 text-sm">
                                <li><span onClick={() => navigate('/template18/contact')} className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">Contact Us</span></li>
                                <li><span onClick={() => navigate('/template18/orders')} className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">Track Order</span></li>
                                <li><span className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">Shipping</span></li>
                                <li><span className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">Returns</span></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-xs tracking-[0.15em] mb-5 text-zinc-500">CONNECT</h4>
                            <ul className="space-y-3 text-sm">
                                <li className="text-zinc-700">hello@avante.in</li>
                                <li className="text-zinc-700">+91 98765 43210</li>
                                <li className="text-zinc-700 pt-2">Mon - Sat: 10am - 7pm</li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-zinc-200 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-zinc-500 text-xs">© 2025 Avante. All rights reserved.</p>
                        <div className="flex gap-6 text-xs text-zinc-500">
                            <span className="hover:text-zinc-900 cursor-pointer transition-colors">Privacy Policy</span>
                            <span className="hover:text-zinc-900 cursor-pointer transition-colors">Terms of Service</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template18;
